import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const LABELS = {
  attendance: "presenze", test_results: "test", fencing: "attività scherma", lessons: "lezioni", bouts: "assalti",
  goals: "obiettivi", injuries: "infortuni", athlete_weapons: "armi", athlete_season_categories: "categorie stagione",
  competitions_invited: "convocazioni gara", competitions_results: "risultati gara", paralympic_profile: "profilo paralimpico",
};

// Doppia conferma esplicita per l'eliminazione definitiva (nessun testo da digitare).
// Se l'atleta ha storico collegato, l'hard delete è bloccato e si propone l'archiviazione.
export const DeleteAthleteDialog = ({ open, onOpenChange, athleteId, label, onConfirm, onArchived, testId = "delete-athlete" }) => {
  const [step, setStep] = useState(1);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !athleteId) return;
    setStep(1); setCheck(null);
    api.get(`/athletes/${athleteId}/delete-check`).then((r) => setCheck(r.data)).catch(() => setCheck({ deletable: true, dependents: {} }));
  }, [open, athleteId]);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); onOpenChange(false); } finally { setBusy(false); }
  };
  const finish = (e) => { e.preventDefault(); run(onConfirm); };
  const archive = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post(`/athletes/${athleteId}/archive`, { reason: "storico collegato" });
      toast.success(`${label} archiviato`);
      onArchived?.();
    });
  };

  const deps = Object.entries(check?.dependents || {}).filter(([, n]) => n > 0);
  const protectedAthlete = check && !check.deletable;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={protectedAthlete ? `${testId}-protected` : `${testId}-step${step}`}>
        {!check ? (
          <AlertDialogHeader>
            <AlertDialogTitle>Verifica storico…</AlertDialogTitle>
            <AlertDialogDescription>Controllo dei dati collegati a {label}.</AlertDialogDescription>
          </AlertDialogHeader>
        ) : protectedAthlete ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>{label} ha storico collegato</AlertDialogTitle>
              <AlertDialogDescription>
                L'eliminazione definitiva non è consentita perché esistono dati tecnici collegati:{" "}
                <strong>{deps.map(([k, n]) => `${n} ${LABELS[k] || k}`).join(", ")}</strong>.
                Puoi <strong>archiviare</strong> l'atleta: resta consultabile nello storico ma viene escluso dalle nuove operazioni.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testId}-cancel-protected`} disabled={busy}>Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid={`${testId}-archive`} onClick={archive} disabled={busy} className="bg-black hover:bg-zinc-800 text-white">
                {busy ? "Archiviazione…" : "Archivia atleta"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : step === 1 ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Eliminazione definitiva: {label}</AlertDialogTitle>
              <AlertDialogDescription>
                Questa operazione è <strong>diversa dall'archiviazione</strong>: {label} verrà rimosso dall'anagrafica di CSP Lab e non comparirà più nelle normali viste. Se vuoi solo sospendere l'atleta, usa l'archiviazione.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testId}-cancel-1`}>Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid={`${testId}-continue`} onClick={(e) => { e.preventDefault(); setStep(2); }} className="bg-black hover:bg-zinc-800">Continua</AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Conferma finale</AlertDialogTitle>
              <AlertDialogDescription>
                Stai per eliminare definitivamente <strong>{label}</strong>. L'azione non può essere annullata.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid={`${testId}-cancel-2`} disabled={busy}>Annulla</AlertDialogCancel>
              <AlertDialogAction data-testid={`${testId}-confirm`} onClick={finish} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white">
                {busy ? "Eliminazione…" : "Elimina definitivamente"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};
